import React from 'react'
import {
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  Link,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { PatientInformationFormProps } from './PatientInformationForm.types'
import { usePatientInformationForm } from './PatientInformationForm.logic'

const PatientInformationForm: React.FC<PatientInformationFormProps> = (
  props
) => {
  const { loading, error, onLegalContentOpen, invitationData } = props
  const {
    formData,
    errors,
    handleChange,
    handleDateChange,
    handleSubmit,
    isFormComplete,
  } = usePatientInformationForm({ onSubmit: props.onSubmit, invitationData })

  // Custom label with clickable legal links
  const ConsentLabel = () => (
    <span>
      I consent to the{' '}
      <Link href="#" onClick={onLegalContentOpen('hipaaConsent')}>
        HIPAA Authorization
      </Link>{' '}
      for Release of Protected Health Information.
    </span>
  )

  return (
    <form onSubmit={handleSubmit}>
      <Typography variant="h3" gutterBottom>
        Patient Wallet Card
      </Typography>

      <Typography variant="body1" paragraph mb={4}>
        Please complete the information below to download your medical device
        wallet card.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            name="firstName"
            label="First Name"
            value={formData.firstName}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.firstName}
            helperText={errors.firstName}
            disabled={loading}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            name="lastName"
            label="Last Name"
            value={formData.lastName}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.lastName}
            helperText={errors.lastName}
            disabled={loading}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date of Birth"
              value={formData.dateOfBirth}
              onChange={handleDateChange}
              disableFuture
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  error: !!errors.dateOfBirth,
                  helperText: errors.dateOfBirth,
                },
              }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                name="consentChecked"
                checked={formData.consentChecked}
                onChange={handleChange}
                color="primary"
              />
            }
            label={<ConsentLabel />}
          />
          {errors.consentChecked && (
            <FormHelperText error>{errors.consentChecked}</FormHelperText>
          )}
        </Grid>

        <Grid item xs={12} p={2}>
          {error && (
            <FormHelperText error sx={{ fontSize: '14px', fontWeight: 'bold' }}>
              Error: {error.message}
            </FormHelperText>
          )}
          {(errors.firstName || errors.lastName || errors.dateOfBirth) && (
            <FormHelperText
              error
              sx={{ fontSize: '14px', fontWeight: 'bold', mt: 1 }}
            >
              Please check the information entered above and try again.
            </FormHelperText>
          )}
        </Grid>

        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading || !isFormComplete}
          >
            {loading ? 'Processing...' : 'Access Wallet Card'}
          </Button>
        </Grid>
      </Grid>
    </form>
  )
}

export default PatientInformationForm
